import { useEffect, useRef, useState } from 'react'
import { Alert, App, Button, Descriptions, Form, Input, InputNumber, Modal, Table, Upload } from 'antd'
import type { UploadFile } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { newIdempotencyKey } from '@/shared/lib/idempotency'
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  receivePurchase,
  uploadSupplierAttachment,
  type Receiving,
} from './supplierApi'

interface PurchaseReceiveModalProps {
  receiving: Receiving | null
  onClose: () => void
}

export function PurchaseReceiveModal({ receiving, onClose }: PurchaseReceiveModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const [quantities, setQuantities] = useState<Record<string, number | null>>({})
  const [note, setNote] = useState('')
  const [files, setFiles] = useState<UploadFile[]>([])
  const [failedFiles, setFailedFiles] = useState<string[]>([])
  const key = useRef('')

  useEffect(() => {
    if (!receiving) return
    key.current = newIdempotencyKey('purchase-receipt')
    setQuantities(Object.fromEntries(receiving.lines.map((line) => [line.id, line.remainingQuantity])))
    setNote('')
    setFiles([])
    setFailedFiles([])
  }, [receiving])

  const lines = (receiving?.lines ?? [])
    .map((line) => ({ purchaseLineId: line.id, quantity: quantities[line.id] ?? 0 }))
    .filter((line) => line.quantity > 0)
  const exceeds = (receiving?.lines ?? []).some((line) => (quantities[line.id] ?? 0) > line.remainingQuantity)

  const receive = useMutation({
    mutationFn: async () => {
      if (!receiving) throw new Error('receiving')
      await receivePurchase(receiving.id, lines, note.trim() ? note.trim() : null, key.current)
      const failed: string[] = []
      for (const file of files) {
        if (!file.originFileObj) continue
        try {
          await uploadSupplierAttachment({ owner: 'receiving', purchaseId: receiving.id }, null, file.originFileObj)
        } catch {
          failed.push(file.name)
        }
      }
      return failed
    },
    onSuccess: (failed) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.supplier.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.stock.all })
      if (failed.length > 0) {
        setFailedFiles(failed)
        return
      }
      onClose()
    },
  })

  const close = () => {
    receive.reset()
    onClose()
  }

  return (
    <Modal
      open={receiving !== null}
      title={t('purchase.receive.title')}
      okText={t('purchase.receive.submit')}
      cancelText={t('common.cancel')}
      onCancel={close}
      onOk={() => receive.mutate()}
      confirmLoading={receive.isPending}
      okButtonProps={{ disabled: lines.length === 0 || exceeds || failedFiles.length > 0 }}
      destroyOnHidden
      width={760}
    >
      <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('purchase.receive.note')} />
      {receive.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(receive.error, t)} />
      ) : null}
      {failedFiles.length > 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('purchase.receive.filesFailed', { files: failedFiles.join(', ') })}
        />
      ) : null}
      {receiving ? (
        <>
          <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label={t('purchase.field.number')}>{receiving.number}</Descriptions.Item>
            <Descriptions.Item label={t('purchase.field.supplier')}>{receiving.supplierName}</Descriptions.Item>
            <Descriptions.Item label={t('purchase.field.warehouse')}>{receiving.warehouse.name}</Descriptions.Item>
            <Descriptions.Item label={t('purchase.field.invoiceNo')}>{receiving.invoiceNo ?? t('common.dash')}</Descriptions.Item>
          </Descriptions>
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={receiving.lines}
            columns={[
              { key: 'description', title: t('purchase.field.description'), render: (_, line) => line.description },
              { key: 'quantity', title: t('purchase.receive.ordered'), align: 'right', render: (_, line) => line.quantity },
              { key: 'received', title: t('purchase.receive.received'), align: 'right', render: (_, line) => line.receivedQuantity },
              { key: 'remaining', title: t('purchase.receive.remaining'), align: 'right', render: (_, line) => line.remainingQuantity },
              {
                key: 'now',
                title: t('purchase.receive.now'),
                width: 150,
                render: (_, line) => (
                  <InputNumber
                    min={0}
                    max={line.remainingQuantity}
                    precision={0}
                    value={quantities[line.id]}
                    status={(quantities[line.id] ?? 0) > line.remainingQuantity ? 'error' : undefined}
                    onChange={(value) => setQuantities((current) => ({ ...current, [line.id]: value }))}
                    style={{ width: '100%' }}
                  />
                ),
              },
            ]}
          />
          <Form layout="vertical" style={{ marginTop: 16 }} disabled={receive.isPending || failedFiles.length > 0}>
            <Form.Item label={t('purchase.receive.remark')}>
              <Input.TextArea rows={2} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} />
            </Form.Item>
            <Form.Item label={t('purchase.receive.deliveryNote')}>
              <Upload
                accept={ATTACHMENT_ACCEPT}
                fileList={files}
                multiple
                beforeUpload={(file) => {
                  if (file.size > ATTACHMENT_MAX_BYTES) {
                    void message.warning(t('supplier.attachments.tooLarge'))
                    return Upload.LIST_IGNORE
                  }
                  setFiles((current) => [...current, { uid: file.uid, name: file.name, originFileObj: file }])
                  return false
                }}
                onRemove={(file) => setFiles((current) => current.filter((f) => f.uid !== file.uid))}
              >
                <Button icon={<UploadOutlined />}>{t('purchase.receive.addDeliveryNote')}</Button>
              </Upload>
            </Form.Item>
          </Form>
        </>
      ) : null}
    </Modal>
  )
}
